import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { getTenantToday } from '../utils/dateUtils';
import { motion } from 'framer-motion';
import {
  Coins,
  RefreshCcw,
  TrendingUp,
  Percent,
  AlertTriangle,
  Flame,
  Clock,
  Layers,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../lib/utils';
import LoanExplainDrawer, { LoanRiskRow } from './LoanExplainDrawer';

import { DateField } from './shared/DateField';

// ============== Animations (mirrors RiskDashboardScreen) ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } },
};

// ============== Shared Components (mirrors RiskDashboardScreen) ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> =
  ({ label, value, icon, color, subText }) => (
    <motion.div
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface Portfolio {
  id: number;
  portfolioName: string;
}

// Risk endpoint per-loan row. Mirrors RiskDashboardScreen's PcLlLoanRiskRow;
// `pikAccrued` is added by a parallel backend change — treat missing as 0.
interface PcLlLoanRiskRow {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sponsorName?: string;
  sector?: string;
  facilityType?: string;
  seniority?: string;
  rating?: string;
  fundedExposure: number;
  unfundedCommitment: number;
  exposureAtDefault: number;
  probabilityDefault: number;
  lossGivenDefault: number;
  recoveryRate: number;
  expectedLoss: number;
  expectedLossPctOfFundedExposure: number;
  spreadBps?: number;
  allInRate?: number;
  spreadDuration: number;
  spreadDv01: number;
  baseRateDv01: number;
  defaultStressLoss: number;
  economicCapital: number;
  pikElectionCurrent?: string;
  pikAccrued?: number;
  nonAccrual?: boolean;
  covenantName?: string;
  covenantCushionValue?: number;
  covenantCushionPct?: number;
  covenantStatus?: string;
  covenantTestDate?: string;
  lmeScore?: number;
  lmeSeverity?: string;
  lmeAssessmentStatus?: string;
}

interface PcLlRiskReport {
  tenantId: number;
  portfolioId: number;
  asOfDate: string;
  persisted: boolean;
  snapshotStatus?: string;
  snapshotType?: string;
  snapshotDate?: string;
  requestedAsOfDate?: string;
  error?: string;
  methodology: string;
  summary: {
    positionCount: number;
    totalFundedExposure: number;
    totalUnfundedCommitment: number;
    totalExposureAtDefault: number;
    nonAccrualCount: number;
    pikElectionCount: number;
  };
  loans: PcLlLoanRiskRow[];
}

// Attribution endpoint per-loan row. Mirrors PerformanceAttributionScreen's
// PcLlAttributionLoan; carries pikIncome + cashInterest for the period.
interface PcLlAttributionLoan {
  loanSource: string;
  loanId: number;
  securityId: number;
  loanName: string;
  borrowerName: string;
  sponsorName?: string;
  sector?: string;
  facilityType?: string;
  beginningMarketValue: number;
  endingMarketValue: number;
  acquisitions: number;
  drawdowns: number;
  disposals: number;
  paydowns: number;
  cashInterest: number;
  oidAccretion: number;
  feeIncome: number;
  pikIncome: number;
  carryPnl: number;
  principalPnl: number;
  realizedPnl: number;
  unrealizedPnlChange: number;
  residual: number;
  totalPnl: number;
  totalReturn: number;
  periodExpectedLoss: number;
  economicCapital: number;
  riskAdjustedPnl: number;
  raroc: number;
  lifecycleDrawdownCount: number;
  lifecyclePaydownCount: number;
  lifecycleAmendmentCount: number;
}

interface PcLlAttributionReport {
  portfolioId: number;
  startDate: string;
  endDate: string;
  methodology: string;
  snapshotStatus?: string;
  error?: string;
  summary: {
    positionCount: number;
    cashInterest: number;
    pikIncome: number;
  };
  loans: PcLlAttributionLoan[];
}

interface MergedPikRow {
  key: string;
  risk: PcLlLoanRiskRow;
  attr?: PcLlAttributionLoan;
  pikAccrued: number;
  pikIncome: number;
  cashInterest: number;
  pikPctOfIncome: number | null;
  isPik: boolean;
  nonAccrual: boolean;
}

// ============== Helpers ==============
// Smart money formatter (M / K / $) — matches the $1.2M / $37.7K house style.
const fmtMoney = (v: number | null | undefined): string => {
  if (v == null || isNaN(v as number)) return '—';
  const n = v as number;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

// Ratio (0..1) → percent string.
const fmtPct1 = (v: number | null | undefined): string =>
  v == null || isNaN(v as number) ? '—' : `${((v as number) * 100).toFixed(1)}%`;

// Mirrors the backend isPikStress(): election string contains "PIK".
const isPikElecting = (election?: string | null): boolean =>
  !!election && election.toUpperCase().includes('PIK');

const PikExposureScreen: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [riskData, setRiskData] = useState<PcLlRiskReport | null>(null);
  const [attributionData, setAttributionData] = useState<PcLlAttributionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asOfDate, setAsOfDate] = useState(getTenantToday());
  const [selectedLoanKey, setSelectedLoanKey] = useState<string | null>(null);

  // The risk snapshot is a point-in-time balance (asOfDate); PIK income accrues
  // over a period, so the attribution leg is read YTD through the same asOfDate.
  const periodStart = useMemo(() => `${asOfDate.slice(0, 4)}-01-01`, [asOfDate]);
  const periodEnd = asOfDate;

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const response = await axios.get('/api/dimensions/portfolios');
        const pList = response.data?.content || response.data || [];
        setPortfolios(Array.isArray(pList) ? pList : []);
        if (pList.length > 0) setSelectedPortfolioId(pList[0].id);
      } catch (err) {
        console.error('Error fetching portfolios:', err);
      }
    };
    fetchPortfolios();
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedPortfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const riskResponse = await axios.get(
        `/api/credit/pc-ll-analytics/risk/portfolio/${selectedPortfolioId}`,
        { params: { asOfDate } }
      );
      setRiskData(riskResponse.data);

      // Attribution is best-effort: it powers PIK income, but a missing period
      // must not blank the whole screen — the risk leg still yields pikAccrued,
      // elections and non-accrual. Keyed the same `${loanSource}-${loanId}`.
      try {
        const attrResponse = await axios.get(
          `/api/credit/pc-ll-analytics/attribution/portfolio/${selectedPortfolioId}`,
          { params: { startDate: periodStart, endDate: periodEnd } }
        );
        setAttributionData(
          attrResponse.data?.snapshotStatus === 'MISSING_SNAPSHOT' ? null : attrResponse.data || null
        );
      } catch (attrErr) {
        console.warn('PC/LL attribution endpoint unavailable:', attrErr);
        setAttributionData(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch PIK exposure analytics');
      setRiskData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId, asOfDate, periodStart, periodEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setSelectedLoanKey(null);
  }, [selectedPortfolioId]);

  // On-demand producer: the GETs never calculate. POST both sanctioned refresh
  // endpoints (risk + attribution), then re-read — same pattern as the Risk
  // Surveillance and Return Attribution screens.
  const handleRecalculate = useCallback(async () => {
    if (!selectedPortfolioId || recalculating) return;
    setRecalculating(true);
    try {
      await axios.post(
        `/api/credit/pc-ll-analytics/risk/portfolio/${selectedPortfolioId}/refresh`,
        null,
        { params: { asOfDate } }
      );
      await axios.post(
        `/api/credit/pc-ll-analytics/attribution/portfolio/${selectedPortfolioId}/refresh`,
        null,
        { params: { startDate: periodStart, endDate: periodEnd } }
      );
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to recalculate PIK exposure snapshot');
    } finally {
      setRecalculating(false);
    }
  }, [selectedPortfolioId, asOfDate, periodStart, periodEnd, recalculating, fetchData]);

  // ---- Join the two feeds on `${loanSource}-${loanId}` ----
  const riskRows = useMemo<PcLlLoanRiskRow[]>(() => riskData?.loans ?? [], [riskData]);

  const attrByKey = useMemo(() => {
    const map = new Map<string, PcLlAttributionLoan>();
    attributionData?.loans?.forEach((l) => map.set(`${l.loanSource}-${l.loanId}`, l));
    return map;
  }, [attributionData]);

  const merged = useMemo<MergedPikRow[]>(() => {
    return riskRows.map((r) => {
      const key = `${r.loanSource}-${r.loanId}`;
      const attr = attrByKey.get(key);
      const pikAccrued = Number(r.pikAccrued ?? 0);
      const pikIncome = Number(attr?.pikIncome ?? 0);
      const cashInterest = Number(attr?.cashInterest ?? 0);
      const denom = pikIncome + cashInterest;
      return {
        key,
        risk: r,
        attr,
        pikAccrued,
        pikIncome,
        cashInterest,
        pikPctOfIncome: attr && denom > 0 ? pikIncome / denom : null,
        isPik: isPikElecting(r.pikElectionCurrent),
        nonAccrual: Boolean(r.nonAccrual),
      };
    });
  }, [riskRows, attrByKey]);

  // "PIK-heavy" = any PIK signal (accruing, earning PIK income, or electing PIK),
  // sorted by PIK accrued descending.
  const pikNames = useMemo(
    () =>
      merged
        .filter((m) => m.pikAccrued > 0 || m.pikIncome > 0 || m.isPik)
        .sort((a, b) => b.pikAccrued - a.pikAccrued),
    [merged]
  );

  // ---- StatCard aggregates ----
  const attributionAvailable = attributionData != null;
  const totalPikAccrued = useMemo(() => merged.reduce((s, m) => s + m.pikAccrued, 0), [merged]);
  const totalPikIncome = useMemo(() => merged.reduce((s, m) => s + m.pikIncome, 0), [merged]);
  const totalCashInterest = useMemo(() => merged.reduce((s, m) => s + m.cashInterest, 0), [merged]);
  const pikPctOfIncome = useMemo(() => {
    const denom = totalPikIncome + totalCashInterest;
    return denom > 0 ? totalPikIncome / denom : null;
  }, [totalPikIncome, totalCashInterest]);
  const nonAccrualCount = riskData?.summary?.nonAccrualCount ?? merged.filter((m) => m.nonAccrual).length;
  const pikAndNonAccrual = useMemo(() => merged.filter((m) => m.isPik && m.nonAccrual), [merged]);
  const pikAndNonAccrualExposure = useMemo(
    () => pikAndNonAccrual.reduce((s, m) => s + Number(m.risk.fundedExposure || 0), 0),
    [pikAndNonAccrual]
  );

  // ---- Drawer binding (loan = matched attribution row, risk = risk row) ----
  const selectedRisk = useMemo(() => {
    if (!selectedLoanKey) return null;
    return riskRows.find((r) => `${r.loanSource}-${r.loanId}` === selectedLoanKey) ?? null;
  }, [selectedLoanKey, riskRows]);

  const selectedAttrLoan = useMemo(() => {
    if (!selectedLoanKey) return null;
    return attributionData?.loans?.find((l) => `${l.loanSource}-${l.loanId}` === selectedLoanKey) ?? null;
  }, [selectedLoanKey, attributionData]);

  const missingSnapshot = riskData?.snapshotStatus === 'MISSING_SNAPSHOT';
  const hasData = !!riskData?.summary && !missingSnapshot;

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <Coins className="w-8 h-8 text-purple-500" />
                PIK Exposure
              </h1>
              <p className="text-slate-500 font-medium mt-1">
                Payment-in-kind accrual, income and non-accrual overlap across the private credit and leveraged-loan book
              </p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1.5 rounded-xl">
                <select
                  value={selectedPortfolioId || ''}
                  onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
                  className="bg-transparent text-xs font-bold text-slate-300 px-3 focus:outline-none cursor-pointer"
                >
                  {portfolios.map((p) => (
                    <option key={p.id} value={p.id} className="bg-slate-900">
                      {p.portfolioName}
                    </option>
                  ))}
                </select>
                <div className="w-px h-4 bg-white/10 mx-2 self-center" />
                <DateField compact
                  value={asOfDate ?? ''}
                  onChange={iso => setAsOfDate(iso)}
                />
              </div>
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn('w-4 h-4', loading && 'animate-spin')} />
                Refresh
              </button>
              <button
                onClick={handleRecalculate}
                disabled={recalculating || !selectedPortfolioId}
                title="Recalculate and publish the PIK (risk + attribution) snapshots, then re-read"
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest"
              >
                <RefreshCcw className={cn('w-4 h-4', recalculating && 'animate-spin')} />
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-300" />
              <span className="flex-1">{error}</span>
              {/* Sanctioned producer path: reads never calculate. */}
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
              >
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          )}

          {/* Staleness banner — the PC/LL feed has its own snapshot store/status. */}
          {riskData?.snapshotStatus === 'LATEST_AVAILABLE' && (
            <div className="flex items-center gap-3 rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-sm font-semibold text-cyan-100">
              <Clock className="h-4 w-4 text-cyan-300" />
              <span className="flex-1">
                Showing latest persisted {riskData.snapshotType || 'risk'} snapshot from {riskData.snapshotDate}
                {riskData.requestedAsOfDate ? ` for requested date ${riskData.requestedAsOfDate}` : ''}.
              </span>
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="rounded-lg border border-cyan-300/40 bg-cyan-400/20 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-cyan-100 transition-colors hover:bg-cyan-400/30 disabled:opacity-50"
              >
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          )}

          {hasData && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard
                label="Total PIK Accrued"
                value={fmtMoney(totalPikAccrued)}
                icon={<Coins className="w-5 h-5" />}
                color="#a855f7"
                subText={`${pikNames.length} PIK-heavy name${pikNames.length === 1 ? '' : 's'}`}
              />
              <StatCard
                label="PIK Income"
                value={attributionAvailable ? fmtMoney(totalPikIncome) : '—'}
                icon={<TrendingUp className="w-5 h-5" />}
                color="#14b8a6"
                subText={attributionAvailable ? `YTD ${periodStart} → ${periodEnd}` : 'Attribution snapshot unavailable'}
              />
              <StatCard
                label="PIK % of Interest Income"
                value={attributionAvailable ? fmtPct1(pikPctOfIncome) : '—'}
                icon={<Percent className="w-5 h-5" />}
                color="#f59e0b"
                subText="PIK ÷ (PIK + cash interest)"
              />
              <StatCard
                label="Non-accrual Names"
                value={String(nonAccrualCount)}
                icon={<AlertTriangle className="w-5 h-5" />}
                color="#ef4444"
                subText="Names flagged non-accrual"
              />
              <StatCard
                label="PIK & Non-accrual Exposure"
                value={fmtMoney(pikAndNonAccrualExposure)}
                icon={<Flame className="w-5 h-5" />}
                color="#ec4899"
                subText={`${pikAndNonAccrual.length} name${pikAndNonAccrual.length === 1 ? '' : 's'} · funded exposure`}
              />
            </div>
          )}
        </motion.div>

        {loading && !riskData ? (
          <GlassCard className="p-6">
            <div className="py-12 text-center text-slate-500 italic text-sm">Loading PIK exposure…</div>
          </GlassCard>
        ) : missingSnapshot ? (
          <GlassCard className="p-6">
            <div className="py-12 text-center text-amber-200 text-sm border-2 border-dashed border-amber-400/20 rounded-2xl bg-amber-500/5 flex flex-col items-center gap-4">
              <span>{riskData?.error || 'PC/LL risk snapshot is not available for this date.'}</span>
              {/* Sanctioned producer path — reads never calculate. */}
              <button
                onClick={handleRecalculate}
                disabled={recalculating}
                className="rounded-lg border border-amber-300/40 bg-amber-400/20 px-4 py-2 text-xs font-bold uppercase tracking-wide text-amber-100 transition-colors hover:bg-amber-400/30 disabled:opacity-50"
              >
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </button>
            </div>
          </GlassCard>
        ) : !hasData ? (
          <GlassCard className="p-6">
            <div className="py-12 text-center text-slate-500 italic text-sm border-2 border-dashed border-white/5 rounded-2xl">
              No private credit or leveraged-loan positions found for this portfolio.
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Layers className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">PIK-heavy Names</h3>
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                persisted to fact_credit_risk: {riskData?.persisted ? 'yes' : 'no'}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Borrower</th>
                    <th className="pb-3 px-2">Type</th>
                    <th className="pb-3 px-2 text-right">PIK Accrued</th>
                    <th className="pb-3 px-2 text-right">PIK Income</th>
                    <th className="pb-3 px-2 text-right">PIK % of Income</th>
                    <th className="pb-3 px-2 text-center">Non-accrual</th>
                    <th className="pb-3 px-2">Election</th>
                    <th className="pb-3 px-2 text-right"></th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {pikNames.map((m) => (
                    <tr
                      key={m.key}
                      onClick={() => setSelectedLoanKey(m.key)}
                      className="group border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-2">
                        <div className="font-bold text-white">{m.risk.borrowerName || m.risk.loanName}</div>
                        <div className="text-[11px] text-slate-500">{m.risk.sponsorName || m.risk.sector || '—'}</div>
                      </td>
                      <td className="py-3 px-2 text-slate-300">{m.risk.loanSource === 'PRIVATE_CREDIT' ? 'PC' : 'LL'}</td>
                      <td className="py-3 px-2 text-right font-mono text-white">{fmtMoney(m.pikAccrued)}</td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">
                        {m.attr ? fmtMoney(m.pikIncome) : '—'}
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-slate-300">{fmtPct1(m.pikPctOfIncome)}</td>
                      <td className="py-3 px-2 text-center">
                        {m.nonAccrual ? (
                          <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest bg-red-500/10 text-red-300 border border-red-500/20">
                            Non-Accrual
                          </span>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {m.risk.pikElectionCurrent ? (
                          <span
                            className={cn(
                              'inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest border',
                              m.isPik
                                ? 'bg-purple-500/10 text-purple-300 border-purple-500/20'
                                : 'bg-slate-700/40 text-slate-300 border-white/10'
                            )}
                          >
                            {m.risk.pikElectionCurrent}
                          </span>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-600 group-hover:text-blue-400 transition-colors">
                          Explain
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </td>
                    </tr>
                  ))}
                  {pikNames.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-10 text-center text-slate-500 italic text-sm">
                        No PIK-electing or PIK-accruing names in this portfolio for {asOfDate}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>

      {/* Explain-every-number drawer. When a matched attribution row exists we pass
          it as `loan` (so the PIK-income / P&L decomposition renders); the risk row
          drives Cards 1–3 + Economic Capital. */}
      <LoanExplainDrawer
        loan={selectedAttrLoan}
        risk={selectedRisk ? (selectedRisk as unknown as LoanRiskRow) : null}
        onClose={() => setSelectedLoanKey(null)}
      />
    </Container>
  );
};

export default PikExposureScreen;
